import { useQuery } from "@tanstack/react-query";
import { useOrgSettings } from "@/lib/org-settings-context";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImpersonateButton } from "@/components/impersonate-button";
import {
  Users,
  Building2,
  CreditCard,
  DollarSign,
  TrendingUp,
  UserCheck,
  Shield,
  BarChart3,
  Package,
  Wallet,
  Globe,
  Sparkles,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Types
interface PlatformStats {
  totalOrganizations: number;
  totalBusinessUnits: number;
  totalCustomers: number;
  totalCards: number;
  totalTransactions: number;
  totalVolumeCents: number;
  totalWalletBalanceCents: number;
  activeIntegrations: number;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  status: string;
  businessUnitCount: number;
  customerCount: number;
  cardCount: number;
}

interface BusinessUnit {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  status: string;
  organizationName: string;
  customerCount: number;
  cardCount: number;
}


function formatCompactNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
}

export default function PlatformAnalytics() {
  const { formatCurrency } = useOrgSettings();
  // Fetch platform-wide stats
  const { data: stats, isLoading: statsLoading } = useQuery<PlatformStats>({
    queryKey: ["platform-stats"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/platform-stats");
      if (!response.ok) throw new Error("Failed to fetch platform stats");
      return response.json();
    },
  });

  // Fetch all program managers
  const { data: organizations, isLoading: pmLoading } = useQuery<Organization[]>({
    queryKey: ["platform-organizations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/all-organizations");
      if (!response.ok) throw new Error("Failed to fetch program managers");
      return response.json();
    },
  });

  // Fetch all business units
  const { data: businessUnits, isLoading: buLoading } = useQuery<BusinessUnit[]>({
    queryKey: ["platform-business-units"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/all-business-units");
      if (!response.ok) throw new Error("Failed to fetch business units");
      return response.json();
    },
  });

  const platformKpis = [
    {
      title: "Organizations",
      value: stats?.totalOrganizations ?? 0,
      icon: Building2,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      title: "Business Units",
      value: stats?.totalBusinessUnits ?? 0,
      icon: Users,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      title: "Customers",
      value: stats?.totalCustomers ?? 0,
      icon: UserCheck,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      title: "Active Cards",
      value: stats?.totalCards ?? 0,
      icon: CreditCard,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
  ];

  const financialKpis = [
    {
      title: "Total Volume",
      value: formatCurrency(stats?.totalVolumeCents ?? 0),
      icon: DollarSign,
      description: "All-time transaction volume",
      isPrimary: true,
    },
    {
      title: "Transactions",
      value: formatCompactNumber(stats?.totalTransactions ?? 0),
      icon: Activity,
      description: "Total processed",
      isPrimary: false,
    },
    {
      title: "Wallet Balance",
      value: formatCurrency(stats?.totalWalletBalanceCents ?? 0),
      icon: Wallet,
      description: "Across all customers",
      isPrimary: false,
    },
  ];

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-display font-bold tracking-tight">Platform Analytics</h1>
          <Badge variant="premium" className="gap-1">
            <Shield className="h-3 w-3" />
            Admin Only
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Platform-wide metrics and entity management. Use the "View as" buttons in the tables below to impersonate users.
        </p>
      </div>

      {/* Platform KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        {platformKpis.map((kpi, index) => (
          <Card key={kpi.title} className="animate-reveal-up" style={{ animationDelay: `${index * 0.1}s` }}>
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className={cn("p-3 rounded-xl", kpi.bgColor)}>
                  <kpi.icon className={cn("h-5 w-5", kpi.color)} />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
                  {statsLoading ? (
                    <Skeleton className="h-7 w-16 mt-1" />
                  ) : (
                    <p className="text-2xl font-display font-bold">{kpi.value.toLocaleString()}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Financial KPIs */}
      <div className="grid gap-6 md:grid-cols-3">
        {financialKpis.map((kpi, index) => (
          <Card
            key={kpi.title}
            variant={kpi.isPrimary ? "stat-gold" : "stat"}
            className={cn(
              "relative overflow-hidden transition-all duration-300 hover:-translate-y-1",
              kpi.isPrimary && "shadow-glow-gold"
            )}
          >
            {kpi.isPrimary && (
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5" />
            )}
            <CardContent className="p-6 relative">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
                    {kpi.isPrimary && <Sparkles className="h-3.5 w-3.5 text-amber-500" />}
                  </div>
                  {statsLoading ? (
                    <Skeleton className="h-10 w-36" />
                  ) : (
                    <p className={cn(
                      "text-3xl font-display font-bold tracking-tight",
                      kpi.isPrimary && "text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500"
                    )}>
                      {kpi.value}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">{kpi.description}</p>
                </div>
                <div className={cn(
                  "p-3 rounded-xl",
                  kpi.isPrimary
                    ? "bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/20"
                    : "bg-primary/10 border border-primary/10"
                )}>
                  <kpi.icon className={cn("h-5 w-5", kpi.isPrimary ? "text-amber-500" : "text-primary")} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Organizations Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Building2 className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <CardTitle>Organizations</CardTitle>
              <CardDescription>All program managers across the platform</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {pmLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Business Units</TableHead>
                  <TableHead className="text-right">Customers</TableHead>
                  <TableHead className="text-right">Cards</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations?.map((pm) => (
                  <TableRow key={pm.id}>
                    <TableCell className="font-medium">{pm.name}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{pm.slug}</TableCell>
                    <TableCell>
                      <Badge variant={pm.status === "ACTIVE" ? "success" : "secondary"}>{pm.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{pm.businessUnitCount}</TableCell>
                    <TableCell className="text-right">{pm.customerCount}</TableCell>
                    <TableCell className="text-right">{pm.cardCount}</TableCell>
                    <TableCell>
                      <ImpersonateButton
                        entityType="ORGANIZATION"
                        entityId={pm.id}
                        entityName={pm.name}
                        variant="ghost"
                        size="sm"
                        showLabel={false}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Business Units Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Users className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <CardTitle>Business Units</CardTitle>
              <CardDescription>All business units across all programs</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {buLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Customers</TableHead>
                  <TableHead className="text-right">Cards</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businessUnits?.map((bu) => (
                  <TableRow key={bu.id}>
                    <TableCell className="font-medium">{bu.name}</TableCell>
                    <TableCell className="text-muted-foreground">{bu.organizationName}</TableCell>
                    <TableCell>
                      <Badge variant={bu.status === "ACTIVE" ? "success" : "secondary"}>{bu.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{bu.customerCount}</TableCell>
                    <TableCell className="text-right">{bu.cardCount}</TableCell>
                    <TableCell>
                      <ImpersonateButton
                        entityType="BUSINESS_UNIT"
                        entityId={bu.id}
                        entityName={bu.name}
                        variant="ghost"
                        size="sm"
                        showLabel={false}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* System Health */}
      <Card variant="elevated">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Globe className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <CardTitle>System Overview</CardTitle>
              <CardDescription>Platform health and integrations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <Package className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-medium">Active Integrations</p>
                <p className="text-2xl font-bold">{stats?.activeIntegrations ?? 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <TrendingUp className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm font-medium">Platform Status</p>
                <p className="text-lg font-semibold text-green-500">Operational</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium">API Version</p>
                <p className="text-lg font-semibold">v1.0.0-rc.3</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
